/**
 * Maps rejection reason codes to human-readable labels.
 */
const REASON_LABEL_MAP: Record<string, string> = {
  insufficient_documentation: "Insufficient documentation",
  unclear_procedures: "Unclear or ambiguous procedures",
  missing_diagnosis: "Missing information to verify diagnosis",
  coding_complexity: "Case too complex for automated coding",
  quality_concerns: "Documentation quality concerns",
};

/**
 * Extracts the rejection reason from a notes string.
 * Notes format: "Reason: ${reason}\nNotes: ${notes}" or just "Reason: ${reason}"
 *
 * @param notes - The notes string that may contain a rejection reason
 * @returns The human-readable rejection reason, or null if not found
 */
export function extractRejectionReason(notes: string | null | undefined): string | null {
  if (!notes?.trim()) {
    return null;
  }

  // Match "Reason: " followed by text until "\nNotes:" or end of string
  const reasonMatch = notes.match(/Reason:\s*(.+?)(?:\nNotes:|$)/s);
  if (!reasonMatch) {
    return null;
  }

  const reasonCode = reasonMatch[1].trim();

  // If it's "other", try to extract the custom reason from notes section
  if (reasonCode === "other") {
    const notesMatch = notes.match(/\nNotes:\s*(.+)$/s);
    if (notesMatch?.[1].trim()) {
      return notesMatch[1].trim();
    }
    return "Other";
  }

  // Map known reason codes to labels
  return REASON_LABEL_MAP[reasonCode] || reasonCode;
}

/**
 * Extracts the reviewer notes from a notes string.
 * Notes format: "Reason: ${reason}\nNotes: ${notes}" or just "Reason: ${reason}"
 *
 * @param notes - The notes string that may contain reviewer notes
 * @returns The reviewer notes, or null if not found
 */
export function extractRejectionNotes(notes: string | null | undefined): string | null {
  if (!notes?.trim()) {
    return null;
  }

  // Match "\nNotes:" followed by the rest of the string
  const notesMatch = notes.match(/\nNotes:\s*(.+)$/s);
  if (!notesMatch) {
    return null;
  }

  const extractedNotes = notesMatch[1].trim();
  return extractedNotes.length > 0 ? extractedNotes : null;
}
